import type { AtsEntityTypeInfo, AtsPullFormat, AtsPullValueFormat } from "@/types/ats-explorer-api"

/**
 * Pure helpers for the Data Pull wizard's field selection. Single-entity pulls send
 * bare paths ("status"). Composite pulls (related entities present) key every path by
 * the entity it belongs to — the primary entity type or a related entity's Name — and
 * send them prefixed ("Placement.status", "Candidate.firstName"), which the API requires.
 */

/** The output key a section's paths are prefixed with in a composite pull */
export function fieldKey(entityKey: string, path: string): string {
  return `${entityKey}.${path}`
}

export function splitFieldKey(key: string): { entityKey: string; path: string } {
  const dot = key.indexOf(".")
  return dot < 0 ? { entityKey: key, path: "" } : { entityKey: key.slice(0, dot), path: key.slice(dot + 1) }
}

/** Whether a stored key belongs to the given entity section */
export function keyBelongsTo(key: string, entityKey: string, isComposite: boolean): boolean {
  return !isComposite || splitFieldKey(key).entityKey === entityKey
}

/** The selection without one section's keys — used when that section's entity type changes */
export function withoutSectionKeys(selected: Iterable<string>, entityKey: string, isComposite: boolean): Set<string> {
  return new Set(Array.from(selected).filter((key) => !keyBelongsTo(key, entityKey, isComposite)))
}

/** The selection with one section's keys re-prefixed — used when a related entity is renamed */
export function renameSectionKeys(selected: Iterable<string>, from: string, to: string): Set<string> {
  return new Set(Array.from(selected).map((key) => (splitFieldKey(key).entityKey === from ? fieldKey(to, splitFieldKey(key).path) : key)))
}

/** The paths selected for one entity section, unprefixed */
export function selectedPathsFor(selected: Iterable<string>, entityKey: string, isComposite: boolean): string[] {
  const keys = Array.from(selected)
  if (!isComposite) return keys
  return keys.filter((key) => splitFieldKey(key).entityKey === entityKey).map((key) => splitFieldKey(key).path)
}

export interface PullFieldsOptions {
  /** Bare paths for single-entity pulls; prefixed keys ("Placement.status") for composite pulls */
  selected: Set<string>
  primaryEntity: AtsEntityTypeInfo | null
  format: AtsPullFormat
}

/**
 * The `fields` to send with a pull. Fields drive the vendor selection when the vendor
 * supports it (Bullhorn) and project CSV columns otherwise; a JSON pull from a vendor
 * without field selection keeps the full payload.
 */
export function buildPullFields({ selected, primaryEntity, format }: PullFieldsOptions): string[] | null {
  if (selected.size === 0) return null
  const projectsColumns = format === "Csv" || format === "Xlsx"
  if (!projectsColumns && !primaryEntity?.supportsFieldSelection) return null
  // Composite selections are already stored as prefixed keys, single-entity ones as bare paths
  return Array.from(selected)
}

/** Fields for the preview listing: the primary entity's own selection, unprefixed */
export function buildPreviewFields(selected: Set<string>, primaryEntity: AtsEntityTypeInfo | null, isComposite: boolean): string[] | null {
  if (!primaryEntity?.supportsFieldSelection || selected.size === 0) return null
  const paths = selectedPathsFor(selected, primaryEntity.name, isComposite)
  return paths.length > 0 ? paths : null
}

/** Friendly values for files people open (CSV/Excel), raw payloads for JSON */
export function defaultValueFormat(format: AtsPullFormat): AtsPullValueFormat {
  return format === "Json" ? "Raw" : "Friendly"
}
